import React from 'react';
import { Star, Zap, Sparkles } from 'lucide-react';
import { 
  calculateXP, 
  getLevelFromXP, 
  getLevelThresholds, 
  calculateProgress,
  getXPToNextLevel,
  type PlayerStats 
} from '@/lib/levelSystem';

interface PlayerLevelBadgeProps {
  points?: number;
  matchesPlayed?: number;
  stats?: PlayerStats | null;
}

const PlayerLevelBadge: React.FC<PlayerLevelBadgeProps> = ({ points = 0, matchesPlayed = 0, stats }) => {
  // Use stats if provided, otherwise construct from points/matchesPlayed for backward compatibility
  const playerStats: PlayerStats = stats || {
    points: points,
    matches: matchesPlayed,
    wins: 0,
    draws: 0,
    goals: 0,
    assists: 0
  };
  
  const currentXP = calculateXP(playerStats);
  const levelInfo = getLevelFromXP(currentXP);
  const progress = calculateProgress(currentXP, levelInfo.level);
  const xpToNext = getXPToNextLevel(currentXP, levelInfo.level);
  const { current } = getLevelThresholds(levelInfo.level);

  return (
    <div className={`premium-level-badge bg-background/60 rounded-xl p-4 border ${levelInfo.borderColor} relative overflow-hidden ${levelInfo.glowColor}`}>
      {/* Animated background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${levelInfo.color} opacity-10 premium-level-bg-pulse`} />
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* Premium Level Icon */}
            <div className={`premium-level-icon relative w-14 h-14 rounded-xl bg-gradient-to-br ${levelInfo.color} flex items-center justify-center text-2xl shadow-lg`}>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-black/20 to-white/20" />
              <span className="relative z-10">{levelInfo.icon}</span>
              {/* Animated ring */}
              <div className={`absolute inset-0 rounded-xl border-2 border-white/30 premium-icon-ring`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl font-black">Level {levelInfo.level}</span>
                <Star className="w-5 h-5 text-yellow-500 drop-shadow-[0_0_6px_rgba(250,204,21,0.6)]" />
              </div>
              <span className={`text-sm font-bold bg-gradient-to-r ${levelInfo.color} bg-clip-text text-transparent`}>
                {levelInfo.title}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1.5 text-primary">
              <Zap className="w-5 h-5 drop-shadow-[0_0_6px_rgba(var(--primary),0.6)]" />
              <span className="font-black text-lg">{currentXP}</span>
            </div>
            <span className="text-xs text-muted-foreground font-medium">Total XP</span>
          </div>
        </div>

        {/* Premium XP Progress Bar */}
        {levelInfo.level < 10 && (
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground font-medium">
              <span className="flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Progress to Level {levelInfo.level + 1}
              </span>
              <span className="font-bold">{Math.round(progress)}%</span>
            </div>
            <div className="premium-xp-bar h-3 bg-muted/30 rounded-full overflow-hidden border border-border/30 relative">
              <div 
                className={`h-full bg-gradient-to-r ${levelInfo.color} rounded-full transition-all duration-1000 ease-out relative`}
                style={{ width: `${progress}%` }}
              >
                {/* Animated shine on progress bar */}
                <div className="absolute inset-0 premium-progress-shine" />
              </div>
              {/* Glow effect */}
              <div 
                className={`absolute top-0 bottom-0 left-0 bg-gradient-to-r ${levelInfo.color} blur-md opacity-50 transition-all duration-1000`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span className="font-medium">{currentXP - current} XP earned</span>
              <span className="font-bold text-primary">{xpToNext} XP to go</span>
            </div>
          </div>
        )}

        {levelInfo.level >= 10 && (
          <div className="text-center py-3 premium-max-level">
            <div className="flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400 animate-pulse" />
              <span className="text-sm font-bold bg-gradient-to-r from-yellow-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                🎉 Max Level Achieved! 🎉
              </span>
              <Sparkles className="w-5 h-5 text-cyan-400 animate-pulse" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerLevelBadge;
